import { useState } from 'react';
import { Alert, Box, Button, Modal, SpaceBetween } from '@amzn/awsui-components-react';
import { AccountFlag } from '@amzn/ivthub-client';
import type { AccountFlagFailure, AccountFlagState } from '../useAccountFlag';

/**
 * The confirmation behind an account's 🗑 Delete on 我的灯塔客户.
 *
 * ⚠️ The removal is GLOBAL — the flag is one per account, not per industry, so the customer leaves
 * the lighthouse list of every industry whose page shows it — and the copy deliberately does NOT
 * say so yet (user, 2026-09-30: leave it out unless the PM asks for it).
 *
 * Clears LIGHTHOUSE only. A BIG BET account stays BIG BET — the two flags are independent writes
 * on the same row, and clearing one never removes the row (see useAccountFlag).
 *
 * Unlike the add dialog, a failure stays IN this dialog: there is one account, and the reader is
 * looking right here. A `failed` one can be retried with the same button; a `denied` one cannot —
 * the button goes, and the message says which role or industry to ask for.
 */

export interface LighthouseDeleteTarget {
  accountId: string;
  accountName?: string;
}

export interface DeleteLighthouseAccountModalProps {
  account: LighthouseDeleteTarget;
  clearFlag: AccountFlagState['clearFlag'];
  onDismiss: () => void;
  /** Called after a successful clear, with the label the account was shown under. */
  onDeleted: (label: string) => void;
}

export function DeleteLighthouseAccountModal({
  account,
  clearFlag,
  onDismiss,
  onDeleted,
}: DeleteLighthouseAccountModalProps) {
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<AccountFlagFailure | null>(null);
  const label = account.accountName ?? account.accountId;

  const confirm = async () => {
    setPending(true);
    setFailure(null);
    const result = await clearFlag(account.accountId, AccountFlag.LIGHTHOUSE);
    setPending(false);
    if (result === null) onDeleted(label);
    else setFailure(result);
  };

  return (
    <Modal
      visible
      // Not dismissable mid-request: the outcome would have nowhere to be shown.
      onDismiss={() => {
        if (!pending) onDismiss();
      }}
      header="删除灯塔客户"
      closeAriaLabel="关闭"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss} disabled={pending}>
              取消
            </Button>
            {failure?.kind !== 'denied' && (
              <Button variant="primary" onClick={() => void confirm()} loading={pending}>
                {failure ? '重试' : '删除'}
              </Button>
            )}
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <Box>确定将「{label}」移出灯塔客户吗？</Box>
        {failure && (
          <Alert type="error" header="删除失败">
            {failure.message}
          </Alert>
        )}
      </SpaceBetween>
    </Modal>
  );
}
